import type { SupabaseClient } from "@supabase/supabase-js";
import type { Dayjs } from "@punchup/utils/dayjs";
import { logException } from "@punchup/sentry";
import type { Database } from "../../supabase.types";

export interface ShowReminderRef {
  settingId: string;
  showId: string;
}

/** Marks the reminder sent only while it is still on, unsent and set for `showId`; true when this call won it. */
export async function claimShowReminder(
  supabase: SupabaseClient<Database>,
  { settingId, showId }: ShowReminderRef,
  { now }: { now: Dayjs }
): Promise<boolean> {
  const at = now.toISOString();
  const { data, error } = await supabase
    .from("performance_notification_settings")
    .update({ reminder_sent_at: at, updated_at: at })
    .eq("id", settingId)
    .eq("send_comedian_reminder", true)
    .eq("show_id", showId)
    .is("reminder_sent_at", null)
    .select("id");

  if (error) {
    logException(error, {
      data: "[show-reminders] Failed to claim a reminder",
      extra: { settingId },
    });
    throw error;
  }

  return (data?.length ?? 0) > 0;
}

/** Switches the reminder off, only while it is still unsent and set for `showId`, so the cron stops re-evaluating it. */
export async function disableShowReminder(
  supabase: SupabaseClient<Database>,
  { settingId, showId }: ShowReminderRef,
  { now }: { now: Dayjs }
): Promise<void> {
  const { error } = await supabase
    .from("performance_notification_settings")
    .update({ send_comedian_reminder: false, updated_at: now.toISOString() })
    .eq("id", settingId)
    .eq("show_id", showId)
    .is("reminder_sent_at", null);

  if (error) {
    logException(error, {
      data: "[show-reminders] Failed to switch a reminder off",
      extra: { settingId },
    });
    throw error;
  }
}
